import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';

const TimeTrackingContext = createContext(null);

export const TimeTrackingProvider = ({ children }) => {
    const { api } = useAuth();
    const [isTracking, setIsTracking] = useState(false);
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [activeProjectId, setActiveProjectId] = useState(null);
    const [activeCustomerId, setActiveCustomerId] = useState(null);
    const [activeCustomerName, setActiveCustomerName] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const intervalRef = useRef(null);

    // Load saved tracking state from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('activeTracking');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                const start = new Date(data.startTime);
                const now = new Date();
                const elapsed = Math.floor((now - start) / 1000);
                
                setIsTracking(true);
                setActiveTaskId(data.taskId);
                setActiveProjectId(data.projectId);
                setActiveCustomerId(data.customerId || null);
                setActiveCustomerName(data.customerName || null);
                setStartTime(start);
                setElapsedSeconds(elapsed);
            } catch (e) {
                localStorage.removeItem('activeTracking');
            }
        }
    }, []);

    // Timer interval
    useEffect(() => {
        if (isTracking && startTime) {
            intervalRef.current = setInterval(() => {
                const now = new Date();
                const elapsed = Math.floor((now - startTime) / 1000);
                setElapsedSeconds(elapsed);
            }, 1000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isTracking, startTime]);

    const startTracking = useCallback((taskId = null, projectId = null, customerId = null, customerName = null) => {
        const now = new Date();
        
        setIsTracking(true);
        setActiveTaskId(taskId);
        setActiveProjectId(projectId);
        setActiveCustomerId(customerId);
        setActiveCustomerName(customerName);
        setStartTime(now);
        setElapsedSeconds(0);

        localStorage.setItem('activeTracking', JSON.stringify({
            taskId,
            projectId,
            customerId,
            customerName,
            startTime: now.toISOString()
        }));
    }, []);

    const stopTracking = useCallback(async (customerId = null, customerName = null, description = '', isPause = false) => {
        if (!isTracking || !startTime) return null;

        const endTime = new Date();

        // Fall back to the customer that was set when the timer was started.
        // This is what makes "track from task" auto-fill the customer in the stop dialog.
        const finalCustomerId = customerId || activeCustomerId;
        const finalCustomerName = customerName || activeCustomerName;

        try {
            const response = await api.post('/time-entries', {
                task_id: activeTaskId,
                project_id: activeProjectId,
                customer_id: finalCustomerId,
                customer_name: finalCustomerName,
                description,
                start_time: startTime.toISOString(),
                end_time: endTime.toISOString(),
                is_pause: isPause
            });

            setIsTracking(false);
            setActiveTaskId(null);
            setActiveProjectId(null);
            setActiveCustomerId(null);
            setActiveCustomerName(null);
            setStartTime(null);
            setElapsedSeconds(0);
            localStorage.removeItem('activeTracking');

            return response.data;
        } catch (error) {
            console.error('Failed to save time entry:', error);
            throw error;
        }
    }, [api, isTracking, startTime, activeTaskId, activeProjectId, activeCustomerId, activeCustomerName]);

    const cancelTracking = useCallback(() => {
        setIsTracking(false);
        setActiveTaskId(null);
        setActiveProjectId(null);
        setActiveCustomerId(null);
        setActiveCustomerName(null);
        setStartTime(null);
        setElapsedSeconds(0);
        localStorage.removeItem('activeTracking');
    }, []);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const value = {
        isTracking,
        activeTaskId,
        activeProjectId,
        activeCustomerId,
        activeCustomerName,
        startTime,
        elapsedSeconds,
        formattedTime: formatTime(elapsedSeconds),
        startTracking,
        stopTracking,
        cancelTracking,
        formatTime
    };

    return (
        <TimeTrackingContext.Provider value={value}>
            {children}
        </TimeTrackingContext.Provider>
    );
};

export const useTimeTracking = () => {
    const context = useContext(TimeTrackingContext);
    if (!context) {
        throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
    }
    return context;
};

export default TimeTrackingContext;
